import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Trophy, TrendingUp, Music, Play, Heart, Share2, 
  ArrowUp, ArrowDown, Minus, ExternalLink, Crown,
  Loader2, AlertCircle, ChevronLeft
} from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';

export default function CreatorChartPage() {
  const { handle } = useParams();
  const { user, token } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requestingTrack, setRequestingTrack] = useState(null);

  useEffect(() => {
    if (handle) {
      fetchCreatorChart();
    }
  }, [handle]);

  const fetchCreatorChart = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API}/charts/creator/${handle}`);
      
      if (!res.ok) {
        if (res.status === 404) {
          setError('Artist not found');
        } else {
          setError('Failed to load chart');
        }
        return;
      }
      
      const data = await res.json();
      setChartData(data);
    } catch (err) {
      console.error('Failed to fetch creator chart:', err);
      setError('Failed to load chart. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestTrack = async (trackId) => {
    if (!token) {
      toast.error('Sign in to request tracks');
      return;
    }

    try {
      setRequestingTrack(trackId);
      const res = await fetch(`${API}/requests/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ track_id: trackId })
      });

      if (res.ok) {
        toast.success('Track requested! 🎵');
        // Refresh chart data
        fetchCreatorChart();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Could not request track');
      }
    } catch (err) {
      toast.error('Failed to request track');
    } finally {
      setRequestingTrack(null);
    }
  };

  const handleShare = () => {
    const shareUrl = window.location.href;
    const shareText = chartData?.share?.title || `Check out ${chartData?.artist?.display_name}'s Top 20`;
    
    if (navigator.share) {
      navigator.share({
        title: shareText,
        url: shareUrl
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const getRankStyle = (position) => {
    if (position === 1) return 'text-4xl text-yellow-500';
    if (position === 2) return 'text-3xl text-gray-300';
    if (position === 3) return 'text-3xl text-orange-400';
    return 'text-2xl text-muted-foreground';
  };

  const getRankBadge = (position) => {
    if (position === 1) return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">🥇 #1</Badge>;
    if (position === 2) return <Badge className="bg-gray-400/20 text-gray-300 border-gray-400/30">🥈 #2</Badge>;
    if (position === 3) return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">🥉 #3</Badge>;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="glass-card p-8 text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{error}</h2>
          <p className="text-muted-foreground mb-4">
            The artist chart could not be loaded.
          </p>
          <Button onClick={() => window.history.back()}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Go Back
          </Button>
        </Card>
      </div>
    );
  }

  const artist = chartData?.artist || {};
  const chart = chartData?.chart || [];
  const chartPeriod = chartData?.chart_period || {};

  return (
    <div className="min-h-screen pb-24" data-testid="creator-chart-page">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-secondary/10" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(var(--primary-rgb),0.15),transparent_50%)]" />
        
        <div className="relative container mx-auto px-4 py-8 sm:py-12">
          {/* Back button */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="mb-6"
            onClick={() => window.history.back()}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Artist Avatar */}
            <div className="relative">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden ring-4 ring-primary/30">
                {artist.avatar_url ? (
                  <img 
                    src={artist.avatar_url} 
                    alt={artist.display_name} 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <Music className="w-12 h-12 text-white" />
                  </div>
                )}
              </div>
              {artist.verified && (
                <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-1">
                  <Crown className="w-4 h-4 text-white" />
                </div>
              )}
            </div>

            {/* Artist Info */}
            <div className="text-center sm:text-left flex-1">
              <div className="flex items-center justify-center sm:justify-start gap-3 mb-2">
                <Trophy className="w-6 h-6 text-yellow-500" />
                <span className="text-sm uppercase tracking-wider text-muted-foreground">
                  Top 20 Chart
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-heading font-black uppercase mb-2">
                {artist.display_name}
              </h1>
              <p className="text-muted-foreground mb-4">
                {artist.handle ? `@${artist.handle}` : ""} • {chartData?.total_tracks || 0} tracks
              </p>
              
              {/* Actions */}
              <div className="flex items-center justify-center sm:justify-start gap-3">
                <Link to={`/artist/${artist.handle}`}>
                  <Button variant="outline" size="sm">
                    View Profile
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={handleShare}
                  data-testid="share-chart-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share Chart
                </Button>
              </div>
            </div>
          </div>

          {/* Chart Period */}
          <div className="mt-6 text-center sm:text-left text-sm text-muted-foreground">
            Chart Period: {new Date(chartPeriod.start).toLocaleDateString()} - {new Date(chartPeriod.end).toLocaleDateString()}
          </div>

          {/* Scoring Info */}
          <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">Scoring:</strong> {chartData?.scoring?.formula}
            </p>
          </div>
        </div>
      </div>

      {/* Chart List */}
      <div className="container mx-auto px-4 py-8">
        {chart.length === 0 ? (
          <Card className="glass-card p-12 text-center">
            <Music className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">No Tracks Yet</h3>
            <p className="text-muted-foreground">
              This artist hasn't uploaded any tracks yet.
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {chart.map((track, index) => (
              <motion.div
                key={track.track_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card 
                  className={`glass-card border-border p-4 sm:p-5 hover:border-primary/30 transition-colors ${
                    track.position <= 3 ? 'ring-1 ring-primary/20' : ''
                  }`}
                  data-testid={`chart-track-${track.position}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Position */}
                    <div className="w-12 text-center flex-shrink-0">
                      <span className={`font-heading font-black ${getRankStyle(track.position)}`}>
                        {track.position}
                      </span>
                    </div>

                    {/* Cover Art */}
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-primary/20 to-secondary/20">
                      {track.cover_art_url ? (
                        <img 
                          src={track.cover_art_url} 
                          alt={track.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="w-6 h-6 text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Track Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-lg truncate">{track.title}</h3>
                        {getRankBadge(track.position)}
                      </div>
                      
                      {/* Stats */}
                      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1" title="Requests (×3 weight)">
                          <TrendingUp className="w-4 h-4 text-secondary" />
                          {track.stats?.requests || 0}
                        </span>
                        <span className="flex items-center gap-1" title="Likes (×2 weight)">
                          <Heart className="w-4 h-4 text-red-400" />
                          {track.stats?.likes || 0}
                        </span>
                        <span className="flex items-center gap-1" title="Plays (×1 weight)">
                          <Play className="w-4 h-4" />
                          {(track.stats?.plays || 0).toLocaleString()}
                        </span>
                      </div>
                      
                      {/* Score */}
                      <div className="mt-1 text-xs text-muted-foreground">
                        Score: <span className="text-primary font-medium">{track.score}</span>
                      </div>
                    </div>

                    {/* Request Button */}
                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        variant={track.position <= 3 ? "default" : "outline"}
                        onClick={() => handleRequestTrack(track.track_id)}
                        disabled={requestingTrack === track.track_id}
                        data-testid={`request-track-${track.track_id}`}
                      >
                        {requestingTrack === track.track_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <TrendingUp className="w-4 h-4 mr-1" />
                            Request
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
